import { Auth } from "aws-amplify";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { useEffect, useState } from "react";

import { Button, FounderIcon, Input, Table } from "..";
import { Username } from "./Username";

import styles from "./styles.module.css";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const getToken = async () => {
  const data = await Auth.currentSession();
  return data.idToken.jwtToken;
};

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [state, setState] = useState({
    name: "",
    email: "",
    phone_number: "",
    tempPassword: "",
    status_icon: false,
  });
  const [trigger, setTrigger] = useState(true);

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [showNewUserSuccessModal, setShowNewUserSuccessModal] = useState(false);
  const handleCloseNewUserSuccessModal = () =>
    setShowNewUserSuccessModal(false);
  const handleShowNewUserSuccessModal = () => setShowNewUserSuccessModal(true);

  const [newUserLoading, setNewUserLoading] = useState(false);
  const [newUserError, setNewUserError] = useState(undefined);

  // Guest dates modal state
  const [guestDatesModal, setGuestDatesModal] = useState({
    show: false,
    user: null,
    dates: [],
    newDate: "",
  });

  useEffect(() => {
    const getUsers = async () => {
      const token = await getToken();

      const usersResponse = await axios.get(serverUrl + "users", {
        headers: {
          "X-Hemingway-Authorization": "Bearer " + token,
        },
      });

      setUsers(
        usersResponse.data
          .map((user) => ({
            ...user,
            unpaid_balance: user.unpaid_balance || false,
            membership_type: user.membership_type || 'full',
            locker_number: user.locker_number || '',
            dues_frequency: user.dues_frequency || '',
            next_due_date: user.next_due_date || '',
            guest_access_dates: user.guest_access_dates || [],
          }))
          .sort((a, b) => {
            const founderDiff = (b.status_icon || 0) - (a.status_icon || 0);

            if (founderDiff === 0) {
              const aName = (a.name || "").trim().toLowerCase();
              const bName = (b.name || "").trim().toLowerCase();
              return aName.localeCompare(bName);
            } else {
              return founderDiff;
            }
          })
      );
    };

    getUsers();
  }, [trigger]);

  const handleChange = (e, key) => {
    setState((prevState) => ({
      ...prevState,
      [key]: e.target.value,
    }));
  };

  function validatePassword(newPassword) {
    const minNumberofChars = 8;
    const regularExpression =
      /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9@#$%^&*]{8,}$/;
    if (newPassword.length < minNumberofChars) {
      alert("Password should contain at least 8 characters");
      return false;
    }
    if (!regularExpression.test(newPassword)) {
      alert(
        "Password should contain at least one number and one special character that's not !"
      );
      return false;
    }
    return true;
  }

  function createDeleteRequest(username) {
    return {
      userName: username,
    };
  }

  const handleDeletePress = async (username) => {
    const token = await getToken();
    axios
      .post(
        serverUrl + "cognito/user/delete",
        createDeleteRequest(username),
        {
          headers: {
            "X-Hemingway-Authorization": "Bearer " + token,
          },
        }
      )
      .then(() => {
        setTrigger(!trigger);
      });
  };

  const handleresetPress = async (user) => {
    const token = await getToken();
    axios.post(
      serverUrl + "/cognito/user/reset_password",
      {
        userName: user.cognito_username,
        password: "TheHadden4U#",
      },
      {
        headers: {
          "X-Hemingway-Authorization": "Bearer " + token,
        },
      }
    );
  };

  const handleNewUser = async () => {
    setNewUserLoading(true);
    setNewUserError(undefined);

    const token = await getToken();

    const user = { ...state };

    const isValidPassword = validatePassword(user.tempPassword);

    if (isValidPassword) {
      try {
        await axios.post(serverUrl + "cognito/user/add", user, {
          headers: {
            "X-Hemingway-Authorization": "Bearer " + token,
          },
        });

        setTrigger(!trigger);
        handleClose();
        handleShowNewUserSuccessModal();
      } catch (error) {
        console.log("Error on create user:", error);
        setNewUserError(error.message);
      }
    }

    setNewUserLoading(false);
  };

  const handleToggleUnpaidBalance = async (user) => {
    const updatedUser = { ...user, unpaid_balance: !user.unpaid_balance };

    try {
      const token = await getToken();

      await axios.post(
        serverUrl + "user/update",
        { ...updatedUser },
        {
          headers: {
            "X-Hemingway-Authorization": "Bearer " + token,
          },
        }
      );

      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.cognito_username === updatedUser.cognito_username ? updatedUser : u
        )
      );
    } catch (error) {
      console.error("Error updating unpaid balance:", error);
    }
  };

  const handleFieldUpdate = async (user, fieldName, newValue) => {
    const updatedUser = { ...user, [fieldName]: newValue };

    try {
      const token = await getToken();

      await axios.post(
        serverUrl + "user/update",
        { ...updatedUser },
        {
          headers: {
            "X-Hemingway-Authorization": "Bearer " + token,
          },
        }
      );

      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.cognito_username === updatedUser.cognito_username ? updatedUser : u
        )
      );
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
    }
  };

  // Open the guest dates modal for a specific user
  const openGuestDatesModal = (user) => {
    setGuestDatesModal({
      show: true,
      user,
      dates: [...(user.guest_access_dates || [])].sort(),
      newDate: "",
    });
  };

  const closeGuestDatesModal = () => {
    setGuestDatesModal({ show: false, user: null, dates: [], newDate: "" });
  };

  const addGuestDate = () => {
    const { newDate, dates } = guestDatesModal;
    if (!newDate || dates.includes(newDate)) return;
    setGuestDatesModal((prev) => ({
      ...prev,
      dates: [...prev.dates, newDate].sort(),
      newDate: "",
    }));
  };

  const removeGuestDate = (date) => {
    setGuestDatesModal((prev) => ({
      ...prev,
      dates: prev.dates.filter((d) => d !== date),
    }));
  };

  const saveGuestDates = async () => {
    const { user, dates } = guestDatesModal;
    await handleFieldUpdate(user, 'guest_access_dates', dates);
    closeGuestDatesModal();
  };

  const columnWidths = [
    "45px",
    undefined,
    undefined,
    undefined,
    "15%",
    "8%",
    "9%",
    "9%",
    "9%",
    "9%",
    "11%",
    "11%",
  ];

  const headers = [
    "",
    "Name",
    "Email",
    "Member Since",
    "Last Check In",
    "Unpaid Balance",
    "Membership",
    "Locker #",
    "Dues Frequency",
    "Next Due Date",
    "",
    "",
  ];

  const rows = users.map((user) => {
    const isLimitedOrGuest =
      user.membership_type === 'limited' || user.membership_type === 'guest';

    return [
      user.status_icon === 1 && (
        <div style={{ marginTop: "2px" }}>
          <FounderIcon />
        </div>
      ),
      <Username user={user} />,
      user.email,
      user.member_since,
      user.checked_in ? new Date(user.checked_in).toLocaleString() : "Never",
      // Unpaid balance toggle
      <input
        type="checkbox"
        checked={user.unpaid_balance}
        onChange={() => handleToggleUnpaidBalance(user)}
      />,
      // Membership type dropdown — includes Guest
      <select
        value={user.membership_type || 'full'}
        onChange={(e) => handleFieldUpdate(user, 'membership_type', e.target.value)}
        style={{ width: "90px", padding: "4px" }}
      >
        <option value="full">Full</option>
        <option value="limited">Limited</option>
        <option value="guest">Guest</option>
      </select>,
      // Locker — hidden for Limited and Guest
      isLimitedOrGuest ? (
        <span style={{ color: "#888", fontSize: "12px" }}>N/A</span>
      ) : (
        <input
          type="text"
          value={user.locker_number}
          onChange={(e) => handleFieldUpdate(user, 'locker_number', e.target.value)}
          style={{ width: "80px", padding: "4px" }}
          placeholder="#"
        />
      ),
      // Dues frequency dropdown
      <select
        value={user.dues_frequency}
        onChange={(e) => handleFieldUpdate(user, 'dues_frequency', e.target.value)}
        style={{ width: "100px", padding: "4px" }}
      >
        <option value="">Select</option>
        <option value="Annual">Annual</option>
        <option value="Monthly">Monthly</option>
      </select>,
      // Next due date input
      <input
        type="date"
        value={user.next_due_date}
        onChange={(e) => handleFieldUpdate(user, 'next_due_date', e.target.value)}
        style={{ width: "130px", padding: "4px" }}
      />,
      // Guest dates button (only for guest type), otherwise Delete
      user.membership_type === 'guest' ? (
        <Button
          style={{
            width: "100px",
            height: "35px",
            marginTop: 0,
            background: "#3F6BBB",
          }}
          onClick={() => openGuestDatesModal(user)}
        >
          Set Dates
          {user.guest_access_dates && user.guest_access_dates.length > 0
            ? ` (${user.guest_access_dates.length})`
            : ""}
        </Button>
      ) : (
        <Button
          style={{
            width: "100px",
            height: "35px",
            marginTop: 0,
            background: "#BB3F3F",
          }}
          onClick={() => {
            if (
              window.confirm(
                "Are you sure you wish to Delete " + user?.email + " ?"
              )
            )
              handleDeletePress(user.cognito_username);
          }}
        >
          Delete
        </Button>
      ),
      // Password Reset button
      <Button
        style={{
          width: "180px",
          height: "35px",
          marginTop: 0,
          marginLeft: 5,
        }}
        onClick={() => {
          if (
            window.confirm(
              "Are you sure you wish to Reset " +
              user?.email +
              " password to TheHadden4U# ?"
            )
          )
            handleresetPress(user);
        }}
      >
        Password Reset
      </Button>,
    ];
  });

  return (
    <>
      <div
        style={{
          marginBottom: "29px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ marginBottom: 0 }}>Users</h3>

        <Button
          style={{ width: "120px", height: "35px", marginTop: 0 }}
          onClick={handleShow}
        >
          New User
        </Button>
      </div>

      <Table
        className={styles.table}
        columnWidths={columnWidths}
        headers={headers}
        rows={rows}
      />

      {/* New User Modal */}
      <Modal show={show} onHide={handleClose} dialogClassName={styles.modal}>
        <Modal.Header closeButton />

        <Modal.Body>
          <h4>New User</h4>

          <Input
            label="Email"
            value={state.email}
            onChange={(e) => handleChange(e, "email")}
          />

          <Input
            label="Temporary Password"
            value={state.tempPassword}
            onChange={(e) => handleChange(e, "tempPassword")}
          />

          <p style={{ color: "red", textAlign: "center", overflow: "auto" }}>
            {newUserError}
          </p>

          <Button
            style={{ width: "100%" }}
            onClick={handleNewUser}
            disabled={newUserLoading}
          >
            {newUserLoading ? (
              <Spinner style={{ marginTop: "3px" }} />
            ) : (
              "Create User"
            )}
          </Button>
        </Modal.Body>
      </Modal>

      {/* New User Success Modal */}
      <Modal
        show={showNewUserSuccessModal}
        onHide={handleCloseNewUserSuccessModal}
        dialogClassName={styles.modal}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <p style={{ textAlign: "center" }}>
            New user: "{state.email}" successfully created
          </p>
        </Modal.Body>
        <Button
          style={{ width: "50%", alignSelf: "center", marginBottom: "20px" }}
          onClick={handleCloseNewUserSuccessModal}
        >
          OK
        </Button>
      </Modal>

      {/* Guest Access Dates Modal */}
      <Modal
        show={guestDatesModal.show}
        onHide={closeGuestDatesModal}
        dialogClassName={styles.modal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Guest Access Dates — {guestDatesModal.user?.name || guestDatesModal.user?.email}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "13px", color: "#555", marginBottom: "16px" }}>
            Add the date(s) this guest has purchased. Access is granted 7:00 AM – 10:00 PM on each date.
          </p>

          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            <input
              type="date"
              value={guestDatesModal.newDate}
              onChange={(e) =>
                setGuestDatesModal((prev) => ({ ...prev, newDate: e.target.value }))
              }
              style={{ padding: "6px", flex: 1 }}
            />
            <Button
              style={{ width: "80px", height: "36px", marginTop: 0 }}
              onClick={addGuestDate}
              disabled={!guestDatesModal.newDate}
            >
              Add
            </Button>
          </div>

          {guestDatesModal.dates.length === 0 ? (
            <p style={{ color: "#888", fontStyle: "italic" }}>No dates added yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {guestDatesModal.dates.map((date) => (
                <li
                  key={date}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <span>{date}</span>
                  <button
                    onClick={() => removeGuestDate(date)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#BB3F3F",
                      cursor: "pointer",
                      fontSize: "16px",
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ width: "100px", height: "36px", marginTop: 0 }}
            onClick={saveGuestDates}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
